import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import Dumbbell from 'highcharts/modules/dumbbell';
import Lollipop from 'highcharts/modules/lollipop';
import Funnel from 'highcharts/modules/funnel';
import WordCloud from 'highcharts/modules/wordcloud';
import Heatmap from 'highcharts/modules/heatmap';
import Sankey from 'highcharts/modules/sankey';
import Treemap from 'highcharts/modules/treemap';
import DependencyWheel from 'highcharts/modules/dependency-wheel';
import SolidGauge from 'highcharts/modules/solid-gauge';
import Accessibility from 'highcharts/modules/accessibility';
import Pyramid3D from 'highcharts/modules/pyramid3d';
import { ZiChartOptions } from '../types/charts/chart-options';
import { useLocation } from 'react-router-dom';
import Highcharts3D from 'highcharts/highcharts-3d';
import Funnel3D from 'highcharts/modules/funnel3d';
import Cylinder from 'highcharts/modules/cylinder';
import VariablePie from 'highcharts/modules/variable-pie';
import XRange from 'highcharts/modules/xrange';
import Bullet from 'highcharts/modules/bullet';

HighchartsMore(Highcharts);
Highcharts3D(Highcharts);
Cylinder(Highcharts);
VariablePie(Highcharts);
XRange(Highcharts);
Bullet(Highcharts);
Funnel3D(Highcharts);
Pyramid3D(Highcharts);
WordCloud(Highcharts);
Funnel(Highcharts);
Dumbbell(Highcharts);
Lollipop(Highcharts);
Heatmap(Highcharts);
Sankey(Highcharts);
Treemap(Highcharts);
DependencyWheel(Highcharts);
SolidGauge(Highcharts);
Accessibility(Highcharts);

type RefObjectForHighchartsReact = {
	/**
	 * Chart reference
	 */
	chart: Highcharts.Chart;
	/**
	 * React reference
	 */
	container: React.RefObject<HTMLDivElement>;
};

export const useHighChart = (
	chartRenderCallback: () => void,
	loadingChartOverride: boolean
): {
	options: ZiChartOptions;
	setOptions: (options: ZiChartOptions) => void;
	chartRender: JSX.Element;
	getChart: () => Highcharts.Chart | undefined;
} => {
	const [chartOptions, setChartOptions] = useState<ZiChartOptions>({});
	const pathname = useLocation().pathname;
	const isDashboardView = pathname.includes('analyze/dashboards/view/');

	const chartRef = useRef<RefObjectForHighchartsReact>(null);

	useEffect(() => {
		if (
			chartRef.current?.chart.chartHeight != chartOptions.height ||
			chartRef.current?.chart.chartWidth != chartOptions.width
		) {
			chartRef.current?.chart.setSize(
				(chartOptions.width as number) ?? 0,
				(chartOptions.height as number) ?? 0
			);
		}
	}, [chartOptions.height, chartOptions.width]);

	useEffect(() => {
		if (
			chartRef.current &&
			chartRef.current?.chart &&
			['wordcloud', 'heatmap', 'treemap', 'sankey'].includes(
				chartOptions.chart?.type as string
			)
		) {
			chartRef.current.chart.reflow();
		}
	}, [chartOptions.series, chartRef.current]);

	void document.fonts.ready.then(() => {
		chartRef.current?.chart.reflow();
	});

	const getChart = useCallback(() => {
		return chartRef.current?.chart;
	}, []);

	const updateOptions = useCallback((options: ZiChartOptions) => {
		setChartOptions(options);
	}, []);

	const chartKey = useMemo(() => {
		const type = chartOptions.chart?.type;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		const seriesHash = JSON.stringify(chartOptions.series?.[0]?.data?.map((d: any) => d));

		// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
		return `${type}-${seriesHash}`;
	}, [chartOptions]);

	const immutable = useMemo(() => {
		// wordcloud requires remount to render correctly
		// Dashboard views use update method and don't need remount
		// Other chart types are handled by Highcharts' native update mechanism
		const type = chartOptions.chart?.type;

		if (type === 'wordcloud') {
			return true;
		}
		if (isDashboardView) {
			return false;
		}

		// For non-dashboard, non-wordcloud charts, allow Highcharts to update smoothly
		// This prevents the flicker that occurs when forcing a remount on chart type change
		return false;
	}, [chartOptions.chart?.type, isDashboardView]);

	const optionsWithSeriesType = useMemo<ZiChartOptions>(() => {
		if (!chartOptions.series) return chartOptions;

		return {
			...chartOptions,
			chart: {
				...chartOptions.chart,
				style: {
					...chartOptions.chart?.style,
					fontFamily: "'Inter', 'RedHatText', 'Overpass', overpass, helvetica",
				},
			},
			series: chartOptions.series.map((s) => ({
				...s,
				type: chartOptions.chart?.type ?? s.type,
			})),
		} as ZiChartOptions;
	}, [chartOptions]);

	return {
		options: chartOptions,
		setOptions: updateOptions,
		chartRender: (
			<div
				style={{
					height: '100%',
					width: '100%',
					visibility: loadingChartOverride ? 'hidden' : 'visible',
				}}
			>
				{optionsWithSeriesType.title &&
					optionsWithSeriesType.series &&
					optionsWithSeriesType.series.length > 0 &&
					optionsWithSeriesType.series[0].type && (
						<HighchartsReact
							ref={chartRef}
							highcharts={Highcharts}
							options={optionsWithSeriesType}
							containerProps={{ style: { height: '100%' } }}
							immutable={immutable}
							callback={chartRenderCallback}
						/>
					)}
			</div>
		),
		getChart,
	};
};
